import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Download, Filter, RefreshCw, Loader2, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PAGE_SIZE = 50;
const SOURCE_LABELS = {
  fg_usn_reprint: 'FG USN Reprint',
  fob_reprint: 'FOB Reprint',
  inhouse_processing_reprint: 'Inhouse Processing',
  inhouse_usn_report_reprint: 'Inhouse USN Report',
  inhouse_directship_reprint: 'Direct-Ship History',
  fg_design_transfer_reprint: 'FG Design Transfer',
  combo_v1_session_reprint: 'Combo V1 Session',
  combo_v2_pp_station_reprint: 'Combo V2 PP Station',
  combo_v2_distributor_reprint: 'Combo V2 Distributor',
  bundle_history_reprint: 'Bundle History',
};

function fmtTs(ts) {
  if (!ts) return '—';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  } catch { return ts; }
}

function SummaryTile({ label, value, sub }) {
  return (
    <Card className="p-4 border-stone-200">
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className="text-3xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs text-stone-500 mt-1">{sub}</div>}
    </Card>
  );
}

export default function USNReplacementLog() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [section, setSection] = useState('');
  const [sourceFlow, setSourceFlow] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const buildQuery = () => {
    const params = new URLSearchParams();
    params.set('page', page); params.set('page_size', PAGE_SIZE);
    if (search) params.set('search', search.trim());
    if (section) params.set('section', section);
    if (sourceFlow) params.set('source_flow', sourceFlow);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params.toString();
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/usn-replacement/log?${buildQuery()}`);
      setRows(r.data.rows || []); setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load log');
    } finally { setLoading(false); }
  };

  const loadSummary = async () => {
    try {
      const r = await apiClient.get('/usn-replacement/summary?days=7');
      setSummary(r.data);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => { loadSummary(); }, []);

  const downloadCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search.trim());
      if (section) params.set('section', section);
      if (sourceFlow) params.set('source_flow', sourceFlow);
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const url = `/usn-replacement/log/export-csv?${params.toString()}`;
      const r = await apiClient.get(url, { responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `usn_replacements_${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      toast.error('CSV download failed');
    }
  };

  const onApply = (e) => { e.preventDefault(); setPage(1); load(); };
  const onReset = () => { setSearch(''); setSection(''); setSourceFlow(''); setDateFrom(''); setDateTo(''); setPage(1); setTimeout(load, 50); };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-h-screen bg-stone-50" data-testid="usn-replacement-log-page">
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-rose-600" />
              USN Replacement Log
            </h1>
            <p className="text-xs text-stone-500">Every reprint click deactivates the old USN and issues a fresh one — full audit here.</p>
          </div>
          <Button variant="outline" onClick={downloadCsv} data-testid="csv-export-btn">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {/* Summary */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-row">
            <SummaryTile label="Today" value={summary.total_today} />
            <SummaryTile label={`Last ${summary.window_days}d`} value={summary.total_in_window} />
            <SummaryTile label="Top Reason" value={summary.top_reasons?.[0]?.value?.slice(0, 24) || '—'} sub={summary.top_reasons?.[0] ? `${summary.top_reasons[0].count} replacements` : ''} />
            <SummaryTile label="Top User" value={summary.top_users?.[0]?.value?.split('@')[0] || '—'} sub={summary.top_users?.[0] ? `${summary.top_users[0].count} replacements` : ''} />
          </div>
        )}

        {/* Filters */}
        <Card className="p-4">
          <form onSubmit={onApply} className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
            <div className="md:col-span-2">
              <label className="text-xs text-stone-500 mb-1 block">Search USN / Replacement ID</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-stone-400" />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="USN-… / REP-…" className="pl-8" data-testid="search-input" />
              </div>
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Section</label>
              <select value={section} onChange={e => setSection(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="section-filter">
                <option value="">All</option>
                <option>FG</option><option>SFG</option><option>FOB</option><option>Inhouse</option><option>Greige</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">Source</label>
              <select value={sourceFlow} onChange={e => setSourceFlow(e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="source-filter">
                <option value="">All</option>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">From</label>
              <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-testid="date-from" />
            </div>
            <div>
              <label className="text-xs text-stone-500 mb-1 block">To</label>
              <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} data-testid="date-to" />
            </div>
            <div className="md:col-span-6 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={onReset} data-testid="reset-filters-btn">Reset</Button>
              <Button type="submit" data-testid="apply-filters-btn"><Filter className="w-4 h-4 mr-1"/>Apply</Button>
            </div>
          </form>
        </Card>

        {/* Table */}
        <Card>
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center text-stone-500 text-sm">No replacements match the current filters.</div>
          ) : (
            <table className="w-full text-sm" data-testid="replacements-table">
              <thead className="bg-stone-50">
                <tr>
                  <th className="text-left p-3">Replacement</th>
                  <th className="text-left p-3">Old USN</th>
                  <th className="text-left p-3">New USN</th>
                  <th className="text-left p-3">Section</th>
                  <th className="text-left p-3">Source</th>
                  <th className="text-left p-3">Reason</th>
                  <th className="text-left p-3">By</th>
                  <th className="text-left p-3">When</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <React.Fragment key={r.replacement_id}>
                    <tr
                      className="border-t hover:bg-stone-50 cursor-pointer"
                      onClick={() => setExpanded(expanded === r.replacement_id ? null : r.replacement_id)}
                      data-testid={`replacement-row-${r.replacement_id}`}
                    >
                      <td className="p-3 font-mono text-xs">
                        <div className="flex items-center gap-1">
                          {r.replacement_id}
                          {r.chain_position > 1 && <Badge variant="outline" className="text-[10px]">#{r.chain_position}</Badge>}
                          {r.was_dispatched && <Badge className="text-[10px] bg-amber-100 text-amber-800 border-amber-300">DISPATCHED</Badge>}
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs"><span className="line-through text-stone-500">{r.old_usn}</span></td>
                      <td className="p-3 font-mono text-xs font-semibold">{r.new_usn}</td>
                      <td className="p-3">{r.section || '—'}</td>
                      <td className="p-3 text-xs">{SOURCE_LABELS[r.source_flow] || r.source_flow}</td>
                      <td className="p-3 text-xs max-w-xs truncate" title={r.reason}>{r.reason}</td>
                      <td className="p-3 text-xs">{r.replaced_by_name || r.replaced_by}</td>
                      <td className="p-3 text-xs whitespace-nowrap">{fmtTs(r.replaced_at)}</td>
                    </tr>
                    {expanded === r.replacement_id && (
                      <tr className="bg-stone-50 border-t" data-testid={`replacement-details-${r.replacement_id}`}>
                        <td colSpan={8} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                            <div>
                              <div className="font-semibold text-stone-700 mb-2">Inherited Properties</div>
                              <pre className="bg-white p-2 rounded border text-[11px] overflow-x-auto">{JSON.stringify(r.inherited_properties || {}, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="font-semibold text-stone-700 mb-2">Cross-references rewritten</div>
                              <pre className="bg-white p-2 rounded border text-[11px] overflow-x-auto">{JSON.stringify(r.cross_references_updated || {}, null, 2)}</pre>
                            </div>
                            <div>
                              <div className="font-semibold text-stone-700 mb-2">Linked documents</div>
                              <pre className="bg-white p-2 rounded border text-[11px] overflow-x-auto">{JSON.stringify(r.linked_documents || {}, null, 2)}</pre>
                              <div className="mt-3 flex flex-col gap-1">
                                <a className="text-blue-600 hover:underline inline-flex items-center gap-1"
                                   href={`/admin/usn-tracker?code=${encodeURIComponent(r.old_usn)}`}
                                   target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-3 h-3" /> Trace old USN
                                </a>
                                <a className="text-blue-600 hover:underline inline-flex items-center gap-1"
                                   href={`/admin/usn-tracker?code=${encodeURIComponent(r.new_usn)}`}
                                   target="_blank" rel="noopener noreferrer">
                                  <ExternalLink className="w-3 h-3" /> Trace new USN
                                </a>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Pagination */}
        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between text-sm">
            <div className="text-stone-500">{total} replacements • Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="prev-page-btn"><ChevronLeft className="w-4 h-4"/></Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="next-page-btn"><ChevronRight className="w-4 h-4"/></Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
